import { useEffect, useState, useCallback } from "react";
import $ from "jquery";
import "datatables.net-dt/css/dataTables.dataTables.min.css";
import "datatables.net";
import { Link } from "react-router-dom";
import DashboardLayout from "../../common/DashboardLayout";
import AdminServices from "../AdminServices";
import { getApiBaseUrl } from "../../../services/httpServices";
import "../../assets/css/custom.css"; // Adjust the path if needed
import { Edit, Eye, Trash2, PlusCircle, UserPlus, Repeat } from "react-feather";
import { Snackbar, Alert } from '@mui/material';

const DEMO_FORM_EMPTY = {
  schoolName: '', email: '', firstName: '', lastName: '', address: '', state: ''
};

const SchoolLists = () => {
  const [schools, setSchools] = useState([]);
  const [activeTab, setActiveTab] = useState('paying'); // paying | accredited | pending | test
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isDeleteConfirmPopup, setIsDeleteConfirmPopup] = useState(false);
  const [confirmedDeleteId, setConfirmedDeleteId] = useState("");
  const [isEditModal, setIsEditModal] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState({
    name: "",
    email: "",
    address: "",
  });
  const [isOpenNotification, setIsOpenNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // --- Demo School State ---
  const [isDemoModal, setIsDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState(DEMO_FORM_EMPTY);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoResult, setDemoResult] = useState(null); // null | { school, credentials }
  const [demoError, setDemoError] = useState('');

  // --- Change Admin State ---
  const [isChangeAdminModal, setIsChangeAdminModal] = useState(false);
  const [changeAdminSchool, setChangeAdminSchool] = useState(null);
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState([]);
  const [adminSearchLoading, setAdminSearchLoading] = useState(false);
  const [selectedNewAdmin, setSelectedNewAdmin] = useState(null);
  const [changeAdminLoading, setChangeAdminLoading] = useState(false);
  const [changeAdminError, setChangeAdminError] = useState('');
  // 'add' grants admin without touching anyone else; 'transfer' demotes the current admin(s).
  const [adminModalMode, setAdminModalMode] = useState('add');
  const [moveConfirmNeeded, setMoveConfirmNeeded] = useState(false);

  const handleDeleteData = async () => {
    try {
      await AdminServices.deleteSchool(confirmedDeleteId);
      setIsDeleteConfirmPopup(false);
      setConfirmedDeleteId("");

      await fetchSchools(); // Re-fetch teachers from backend

      if ($.fn.DataTable.isDataTable("#schoolsTable")) {
        $("#schoolsTable").DataTable().destroy(); // ✅
      }
    } catch (error) {
      console.log("Failed to delete teacher:", error);
      setIsDeleteConfirmPopup(false);
      setConfirmedDeleteId("");
    }
  }

  const handleCloseNotification = () => {
    setIsOpenNotification(false);
    setSuccessMessage("");
    setErrorMessage("");
  }

  const handleEditSchool = (type, e) => {
    setSelectedSchool({
      ...selectedSchool,
      [type]: e.target.value
    })
  }

  const isValid = () => {
    if (
      !selectedSchool.name.trim() ||
      !selectedSchool.address.trim()
    ) {
      setIsOpenNotification(true);
      setErrorMessage("School name and address are required");
      return false;
    }

    return true;
  };

  const handleUpdate = async () => {
    if (!isValid()) return;

    if (isLoading) return;

    setIsLoading(true);

    try {
      const updatedSchool = await AdminServices.updateSchool(selectedSchool);

      if (updatedSchool) {
        setSuccessMessage("School updated successfully!");
        setIsOpenNotification(true);
        setIsEditModal(false);
        await fetchSchools(); // Re-fetch schools from backend

        if ($.fn.DataTable.isDataTable("#schoolsTable")) {
          $("#schoolsTable").DataTable().destroy(); // ✅
        }
      }
      setIsLoading(false);
    } catch (err) {
      setIsOpenNotification(true);
      setErrorMessage(err?.response?.data?.error || err);
      setIsLoading(false);
    }
  }

  // --- Reclassify a school between dashboard tabs (Paying/Accredited/Pending/Test) ---
  /**
   * Which tab a school belongs under.
   *
   * 'free' has no tab of its own on purpose. A centre the Foundation gave its
   * place to is a provisioned, full-access customer, so it belongs beside the
   * paying ones rather than in a category people have to go looking for -
   * After 3's three free locations sat under Pending reading N/A in every
   * column, which looks like three abandoned registrations.
   *
   * A school with no customerStatus set still counts as pending.
   */
  const inTab = (school, tab) => {
    const status = school.customerStatus || 'pending';
    if (tab === 'paying') return status === 'paying' || status === 'free';
    return status === tab;
  };

  const handleSetStatus = async (schoolId, customerStatus) => {
    try {
      await AdminServices.setCustomerStatus(schoolId, customerStatus);
      setSuccessMessage(`Moved to "${customerStatus}"`);
      setIsOpenNotification(true);
      await fetchSchools(); // refresh so counts/tabs update
    } catch (err) {
      /**
       * Say which kind of failure it was.
       *
       * "Failed to update status" was shown for everything, including the case
       * where the request never reached a server at all - a dead staging API,
       * a blocked preflight, a dropped connection. Dave hit exactly that on 22
       * Oct 2026 and the message gave him nothing to act on, while the real
       * causes (403 "super admin required", 401 "token expired") would both
       * have been self-explanatory had they been the problem.
       *
       * Three outcomes, because they need three different actions:
       *
       *  - a reply arrived      -> show what the server said (403, 404, 401...)
       *  - no reply arrived     -> name the URL; the app is usually talking to an
       *                            environment nobody meant it to
       *  - nothing was sent     -> a fault in THIS page, so say so and quote it
       *
       * The third case used to be reported as the second, which is how a plain
       * TypeError masqueraded as a dead API for a day: setCustomerStatus was
       * added to services/AdminServices.jsx while this screen imports the twin at
       * components/admin/AdminServices.jsx, so the call was undefined and threw
       * before any request existed. "Could not reach the server" sent us hunting
       * CORS, DNS and the gateway while the server had never been asked anything.
       */
      setErrorMessage(
        err?.response?.data?.error
        || (err?.response
          ? `Server refused the change (HTTP ${err.response.status}).`
          : err?.request
            ? `Could not reach the server at ${getApiBaseUrl()} — the request never arrived. `
              + 'Check the Network tab for the failed request.'
            : `This page failed before it could send the request: ${err?.message || err}`)
      );
      setIsOpenNotification(true);
    }
  };

  // --- Create Demo School ---
  const handleCreateDemoSchool = async () => {
    setDemoError('');
    const { schoolName, email, firstName, lastName, address, state } = demoForm;
    if (!schoolName || !email || !firstName || !lastName || !address || !state) {
      setDemoError('All fields are required.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setDemoError('Please enter a valid email address.');
      return;
    }
    setDemoLoading(true);
    try {
      const result = await AdminServices.createDemoSchool(demoForm);
      setDemoResult(result);
    } catch (err) {
      setDemoError(err?.response?.data?.error || err?.message || 'Failed to create demo school.');
    } finally {
      setDemoLoading(false);
    }
  };

  const closeDemoModal = () => {
    setIsDemoModal(false);
    setDemoForm(DEMO_FORM_EMPTY);
    setDemoResult(null);
    setDemoError('');
    if (demoResult) fetchSchools(); // refresh list if a school was created
  };

  // --- Change Admin Handlers ---
  const openChangeAdminModal = (school, mode = 'add') => {
    setChangeAdminSchool(school);
    setAdminModalMode(mode);
    setIsChangeAdminModal(true);
    setAdminSearchQuery('');
    setAdminSearchResults([]);
    setSelectedNewAdmin(null);
    setChangeAdminError('');
    setMoveConfirmNeeded(false);
  };

  const closeChangeAdminModal = () => {
    setIsChangeAdminModal(false);
    setChangeAdminSchool(null);
    setAdminSearchQuery('');
    setAdminSearchResults([]);
    setSelectedNewAdmin(null);
    setChangeAdminError('');
    setMoveConfirmNeeded(false);
  };

  const handleAdminSearch = async (query) => {
    setAdminSearchQuery(query);
    setSelectedNewAdmin(null);
    if (!query || query.length < 2) {
      setAdminSearchResults([]);
      return;
    }
    setAdminSearchLoading(true);
    try {
      const response = await AdminServices.searchUsers(query);
      // Filter to only show teacher/user type results (not schools)
      const users = (response.users || []).filter(u => u.type === 'teacher');
      setAdminSearchResults(users);
    } catch (err) {
      console.error("Admin search error:", err);
      setAdminSearchResults([]);
    } finally {
      setAdminSearchLoading(false);
    }
  };

  const handleChangeAdmin = async (forceMove = false) => {
    if (!selectedNewAdmin || !changeAdminSchool) return;
    setChangeAdminLoading(true);
    setChangeAdminError('');
    try {
      if (adminModalMode === 'transfer') {
        await AdminServices.changeSchoolAdmin({
          schoolId: changeAdminSchool._id,
          newAdminUserId: selectedNewAdmin.id
        });
        setSuccessMessage(`Admin transferred to ${selectedNewAdmin.email} for ${changeAdminSchool.name}`);
      } else {
        await AdminServices.addSchoolAdmin({
          schoolId: changeAdminSchool._id,
          userId: selectedNewAdmin.id,
          moveFromOtherSchool: forceMove
        });
        setSuccessMessage(`${selectedNewAdmin.email} added as an admin of ${changeAdminSchool.name}`);
      }
      setIsOpenNotification(true);
      closeChangeAdminModal();
      await fetchSchools();
      if ($.fn.DataTable.isDataTable("#schoolsTable")) {
        $("#schoolsTable").DataTable().destroy();
      }
    } catch (err) {
      // 409 = the user belongs to another school; offer an explicit move instead of failing.
      if (adminModalMode === 'add' && err?.response?.status === 409) {
        setMoveConfirmNeeded(true);
      }
      setChangeAdminError(err?.response?.data?.error || err?.message || 'Failed to update admin');
    } finally {
      setChangeAdminLoading(false);
    }
  };

  const fetchSchools = useCallback(async () => {
    try {
      const response = await AdminServices.getAllSchools({
        page: currentPage,
        search,
      });
      console.log("getAllSchools response: ", response);

      // Log payment data for debugging
      if (response.schools && response.schools.length > 0) {
        console.log("Sample school payment data:", response.schools.slice(0, 5).map(school => ({
          name: school.name,
          email: school.email,
          payment: school.payment ? {
            isPaid: school.payment.isPaid,
            // NEW FIELDS - Use these
            monthlyAmount: school.payment.monthlyAmount,
            nextDueDate: school.payment.nextDueDate,
            paymentStatus: school.payment.paymentStatus,
            // SUBSCRIPTION TRACKING FIELDS
            totalPaidToDate: school.payment.totalPaidToDate,
            lastPaymentDate: school.payment.lastPaymentDate,
            lastPaymentAmount: school.payment.lastPaymentAmount,
            autoRenew: school.payment.autoRenew,
            renewalReminderSent: school.payment.renewalReminderSent,
            renewalReminderDate: school.payment.renewalReminderDate,
            // OLD FIELDS - For reference only
            displayAmount: school.payment.displayAmount,
            displayDate: school.payment.displayDate,
            displayAmountDue: school.payment.displayAmountDue,
            nextPaymentDueDate: school.payment.nextPaymentDueDate,
            nextPaymentAmount: school.payment.nextPaymentAmount,
            displayNextPaymentDate: school.payment.displayNextPaymentDate,
            renewalDate: school.payment.renewalDate,
            endDate: school.payment.endDate,
            installmentsPaid: school.payment.installmentsPaid,
            installmentsTotal: school.payment.installmentsTotal,
            totalAmount: school.payment.totalAmount,
            amount: school.payment.amount,
            isProcessed: school.payment.isProcessed,
            status: school.payment.status
          } : null
        })));

      }

      setSchools(response.schools || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error("Error fetching schools:", error);
    }
  }, [currentPage, search]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  useEffect(() => {
    if (schools.length === 0) return; // Prevent initializing if no data

    if ($.fn.DataTable.isDataTable("#schoolsTable")) {
      $("#schoolsTable").DataTable().destroy(); // Destroy old instance before reinitializing
    }

    $("#schoolsTable").DataTable({
      responsive: true,
      autoWidth: false,
      paging: true, // ✅ Enable Pagination
      searching: true,
      ordering: true,
      pageLength: 25, // ✅ Set default entries per page
      lengthMenu: [[25, 50, -1], [25, 50, "All"]], // ✅ Add pagination options
    });

    $('#schoolsTable').closest('.dt-layout-table').addClass('dt-custom-table');
  }, [schools, activeTab]); // Re-init when the data OR the active customer tab changes

  return (
    <DashboardLayout
      role="admin"
      layoutType="topnav"
      activeMenu="Schools List"
      showBreadcrumbs={false}
    >
      <div className="col-xl-12 col-lg-12">

        {/* Create Demo School Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={() => { setIsDemoModal(true); setDemoResult(null); setDemoError(''); }}
            style={{ gap: 6 }}
          >
            <PlusCircle size={16} /> Create Demo School
          </button>
        </div>

        {/* Customer status tabs */}
        <div className="customer-status-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { key: 'paying', label: 'Paid' },
            { key: 'accredited', label: 'Accredited' },
            { key: 'pending', label: 'Pending' },
            { key: 'test', label: 'Test' },
          ].map((t) => {
            const count = schools.filter((s) => !s.isadmin && inTab(s, t.key)).length;
            return (
              <button
                key={t.key}
                type="button"
                className={`btn ${activeTab === t.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => {
                  // Tear down DataTables BEFORE the state change so React re-renders a clean
                  // table. Otherwise React reconciles a DataTables-mutated DOM and throws
                  // (blank page) on every tab except the initially-rendered one.
                  if ($.fn.DataTable.isDataTable("#schoolsTable")) {
                    $("#schoolsTable").DataTable().destroy();
                  }
                  setActiveTab(t.key);
                }}
              >
                {t.label}
                <span className="badge bg-light text-dark ms-2">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Table with DataTables */}
        <div className="row">
          <div className="custom-table">
            <table id="schoolsTable" className="table table-nowrap mb-0">
              <thead>
                <tr>
                  <th>Action</th>
                  <th></th>
                  <th>School Name</th>
                  <th>Email</th>
                  <th>Date</th>
                  <th>Payment</th>
                  <th>Progress</th>
                  <th>Total Paid</th>
                  <th>Payment Date</th>
                  <th>Renewal Date</th>
                  <th>Auto-Renew</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {schools.filter((school) => !school.isadmin && inTab(school, activeTab)).map((school, index) => (
                  <tr key={school._id}>
                    <td>
                      <Link to={`/admin/school-lists?search=${encodeURIComponent(school.name)}`}>
                        <Eye className="view-icon" />
                      </Link>
                      <Edit className="edit-icon" onClick={() => {
                        setSelectedSchool(school);
                        setIsEditModal(true);
                      }} />
                      <UserPlus className="edit-icon" title="Add Admin (keeps existing admins)" onClick={() => openChangeAdminModal(school, 'add')} />
                      <Repeat className="edit-icon" title="Transfer Admin (demotes existing admins)" onClick={() => openChangeAdminModal(school, 'transfer')} />
                      <Trash2 className="delete-data" onClick={() => {
                        setConfirmedDeleteId(school._id);
                        setIsDeleteConfirmPopup(true);
                      }} />
                      <select
                        value={school.customerStatus || 'pending'}
                        onChange={(e) => handleSetStatus(school._id, e.target.value)}
                        title="Move this school to another tab"
                        className="form-select form-select-sm mt-1"
                        style={{ width: 'auto', display: 'inline-block', fontSize: '12px' }}
                      >
                        <option value="paying">Paid</option>
                        <option value="free">Free</option>
                        <option value="accredited">Accredited</option>
                        <option value="pending">Pending</option>
                        <option value="test">Test</option>
                      </select>
                    </td>
                    <td>{index + 1}</td>
                    <td>
                      {school.name}
                    </td>
                    <td>
                      {school.email}
                    </td>
                    <td>
                      {new Date(school.createdAt).toLocaleString()}
                    </td>
                    <td>
                      {school.payment ? (() => {
                        // Try new fields first, then fallback to old fields
                        const monthlyAmount = school.payment.monthlyAmount ||
                          school.payment.installmentAmount ||
                          school.payment.nextPaymentAmount ||
                          0;

                        const nextDueDate = school.payment.nextDueDate
                          ? new Date(school.payment.nextDueDate)
                          : (school.payment.nextPaymentDueDate
                            ? new Date(school.payment.nextPaymentDueDate)
                            : null);

                        const isNextDueOverdue = nextDueDate && nextDueDate < new Date();
                        // Use backend-provided fields directly
                        const paymentStatus = school.payment.paymentStatus ||
                          (!school.payment.isPaid ? "Unpaid" : "Paid");

                        // Use backend-provided isPaid/isProcessed fields (backend calculates this correctly)
                        const isProcessed = school.payment.isProcessed === true ||
                          school.payment.isPaid === true;

                        // For processed installment plans, show monthly amount and next due date
                        // Check if it's an installment plan (planType === 'installment' OR has installmentsTotal > 1)
                        // Billed monthly. This read planType === 'installment' with a
                        // fallback on installmentsTotal; the rows are renamed but kept
                        // that field, so the fallback is what has been carrying it.
                        const isInstallmentPlan = school.payment.planType === 'monthly' ||
                          (school.payment.installmentsTotal && school.payment.installmentsTotal > 1);


                        if (isProcessed && isInstallmentPlan && monthlyAmount > 0) {
                          if (nextDueDate) {
                            return (
                              <div>
                                <div className={isNextDueOverdue ? "text-danger" : "text-info"}>
                                  ${Number(monthlyAmount).toFixed(2)}/month
                                </div>
                                <div className={`small mt-1 ${isNextDueOverdue ? "text-danger" : "text-muted"}`}>
                                  {isNextDueOverdue ? "⚠️ " : ""}
                                  Due: {nextDueDate.toLocaleDateString()}
                                </div>
                              </div>
                            );
                          } else {
                            // Has monthly amount but no due date
                            return (
                              <div>
                                <div className="text-info">
                                  ${Number(monthlyAmount).toFixed(2)}/month
                                </div>
                                <div className="small text-muted mt-1">
                                  {paymentStatus}
                                </div>
                              </div>
                            );
                          }
                        }

                        // For unprocessed payments, show "Unpaid" with amount
                        if (!isProcessed) {
                          const totalAmount = school.payment.totalAmount ||
                            school.payment.displayAmount ||
                            school.payment.amount ||
                            0;
                          return (
                            <div>
                              <span className="text-danger">
                                Unpaid
                              </span>
                              {totalAmount > 0 && (
                                <div className="small text-danger mt-1">
                                  Amount Due: ${Number(totalAmount).toFixed(2)}
                                </div>
                              )}
                            </div>
                          );
                        }

                        // For completed payments, show status
                        return (
                          <div>
                            <span className="text-success">
                              {paymentStatus}
                            </span>
                            {monthlyAmount > 0 && (
                              <div className="small text-info mt-1">
                                ${Number(monthlyAmount).toFixed(2)}
                              </div>
                            )}
                            {/* Show last payment info if available */}
                            {school.payment.lastPaymentDate && school.payment.lastPaymentAmount && (
                              <div className="small text-muted mt-1">
                                Last: ${Number(school.payment.lastPaymentAmount).toFixed(2)} on {new Date(school.payment.lastPaymentDate).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        );
                      })() : "N/A"}
                    </td>
                    <td>
                      {/* Progress Column - Show X/12 payments for installment plans */}
                      {school.payment?.planType === 'installment' && school.payment?.installmentsTotal ? (
                        <div>
                          <div className="text-info">
                            {school.payment.installmentsPaid || 0}/{school.payment.installmentsTotal}
                          </div>
                          {school.payment.totalAmount && school.payment.totalPaidToDate !== undefined && (
                            <div className="small text-muted mt-1">
                              {Math.round(((school.payment.totalPaidToDate || 0) / school.payment.totalAmount) * 100)}% paid
                            </div>
                          )}
                        </div>
                      ) : school.payment?.isPaid === true ? (
                        <span className="badge bg-success">Complete</span>
                      ) : "N/A"}
                    </td>
                    <td>
                      {/* Total Paid Column - Show totalPaidToDate / totalAmount */}
                      {school.payment?.totalAmount ? (() => {
                        const totalPaid = school.payment.totalPaidToDate || 0;
                        const totalAmount = school.payment.totalAmount;
                        const remaining = totalAmount - totalPaid;

                        return (
                          <div>
                            <div className="text-success">
                              ${Number(totalPaid).toFixed(2)}
                            </div>
                            <div className="small text-muted">
                              of ${Number(totalAmount).toFixed(2)}
                            </div>
                            {/* No "remaining" on a subscription that renews until
                                cancelled. This was gated on the monthly plan's former
                                name, so it stopped rendering when the rows were
                                renamed - accidentally the right outcome. Removed
                                rather than left as a check that only works by being
                                broken. */}
                          </div>
                        );
                      })() : "N/A"}
                    </td>
                    <td>
                      {/* Prioritize lastPaymentDate, then nextDueDate, then other dates */}
                      {school.payment?.lastPaymentDate ? (
                        <div>
                          <div>{new Date(school.payment.lastPaymentDate).toLocaleDateString()}</div>
                          <div className="small text-muted">Last payment</div>
                        </div>
                      ) : school.payment?.nextDueDate ? (
                        <div>
                          <div>{new Date(school.payment.nextDueDate).toLocaleDateString()}</div>
                          {new Date(school.payment.nextDueDate) < new Date() && (
                            <span className="badge bg-danger small">Overdue</span>
                          )}
                        </div>
                      ) : school.payment?.displayDate ? (
                        new Date(school.payment.displayDate).toLocaleString()
                      ) : school.payment?.startDate ? (
                        new Date(school.payment.startDate).toLocaleString()
                      ) : school.payment?.isPaid === false ? (
                        "Due"
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>
                      {/* Use new field: nextDueDate (shows renewal date for completed, next payment for active) */}
                      {school.payment?.nextDueDate ? (() => {
                        const dueDate = new Date(school.payment.nextDueDate);
                        const today = new Date();
                        const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                        // A subscription that auto-renews does not expire, so this
                        // date is its next charge and carries no urgency. Both plans
                        // renew until cancelled, and the Auto-Renew column sits
                        // immediately to the right saying so.
                        //
                        // The old test was `planType !== 'installment' || installmentsPaid
                        // >= installmentsTotal`. While these rows said "installment" the
                        // first clause was false and the badge stayed hidden. Renaming
                        // them to "monthly" made it true, and every monthly subscriber
                        // started showing an expiry warning against their next monthly
                        // charge - a school that signed up two weeks ago reading as
                        // "17 days" from expiry.
                        const willAutoRenew = school.payment.autoRenew !== false ||
                          !!school.payment.recurringTransactionId;
                        const isExpired = !willAutoRenew && daysUntilDue < 0;
                        const isExpiringSoon = !willAutoRenew &&
                          daysUntilDue <= 30 && daysUntilDue >= 0 &&
                          school.payment.isPaid === true;

                        return (
                          <div>
                            <div>{dueDate.toLocaleDateString()}</div>
                            {isExpired && (
                              <span className="badge bg-danger small">Expired</span>
                            )}
                            {isExpiringSoon && (
                              <span className="badge bg-warning small">{daysUntilDue} days</span>
                            )}
                          </div>
                        );
                      })() : "N/A"}
                    </td>
                    <td>
                      {/* Auto-Renew Column */}
                      {/*
                        An accredited centre is billed by the on-site training
                        company, not by us, so it has no subscription and never
                        will. "Manual" was technically true and actively
                        misleading - it reads as a billing problem somebody
                        ought to fix. Accredited is the answer, and it is good
                        news, so it is green.
                      */}
                      {school.customerStatus === 'accredited' ? (
                        <span className="badge bg-success">
                          <i className="bx bx-award me-1"></i>
                          Accredited
                        </span>
                      ) : school.customerStatus === 'free' ? (
                        /*
                          Granted by the Foundation. Authorize.Net will not take
                          a subscription for $0, so there is nothing recurring
                          and nothing manual about it - and nothing anyone needs
                          to chase.
                        */
                        <span className="badge bg-info">
                          <i className="bx bx-gift me-1"></i>
                          Free
                        </span>
                      ) : school.payment ? (
                        school.payment.autoRenew !== false ? (
                          <span className="badge bg-success">
                            <i className="bx bx-check-circle me-1"></i>
                            Auto-Renew
                          </span>
                        ) : (
                          <span className="badge bg-warning">
                            <i className="bx bx-x-circle me-1"></i>
                            Manual
                          </span>
                        )
                      ) : "N/A"}
                    </td>
                    <td>{school.ip ?? "N/A"}</td>
                  </tr >
                ))}
              </tbody >
            </table >
          </div >
        </div >
      </div >
      {
        isDeleteConfirmPopup ?
          (<div className="delete-confirm-popup" >
            <div className="popup-content">
              <p>Are you sure you want to delete this school?</p>
              <div className="popup-buttons">
                <button onClick={handleDeleteData}>Yes</button>
                <button onClick={() => {
                  setConfirmedDeleteId("");
                  setIsDeleteConfirmPopup(false);
                }}>Cancel</button>
              </div>
            </div>
          </div>)
          :
          <></>
      }
      {
        isEditModal ?
          (<div className="edit-modal">
            <div className="modal-content">
              <h4>Update Info</h4>
              <div className="modal-input-group">
                <label>School Name</label><br />
                <input value={selectedSchool.name} onChange={(e) => handleEditSchool("name", e)} />
              </div>
              <div className="modal-input-group">
                <label>Email (Read-only - managed by school admin account)</label><br />
                <input value={selectedSchool.email} disabled style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }} />
              </div>
              <div className="modal-input-group">
                <label>Address</label><br />
                <input value={selectedSchool.address} onChange={(e) => handleEditSchool("address", e)} />
              </div>
              <div className="modal-buttons">
                <button onClick={handleUpdate}>Update</button>
                <button onClick={() => {
                  setSelectedSchool({
                    name: "",
                    email: "",
                    address: "",
                  });
                  setIsEditModal(false);
                }}>Cancel</button>
              </div>
            </div>
          </div>)
          :
          <></>
      }
      {/* Demo School Modal */}
      {isDemoModal && (
        <div className="edit-modal" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: 520, width: '95%', padding: 28 }}>
            {!demoResult ? (
              <>
                <h4 style={{ marginBottom: 20 }}>🏫 Create Demo School Account</h4>
                <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>
                  Creates a school, admin user, and active payment record. Login password will be <strong>angelwatch</strong>.
                </p>

                {demoError && (
                  <div style={{ background: '#fff0f0', border: '1px solid #ffcdcd', borderRadius: 6, padding: '10px 14px', marginBottom: 14, color: '#c0392b', fontSize: 14 }}>
                    {demoError}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div className="modal-input-group" style={{ gridColumn: '1 / -1' }}>
                    <label>School Name *</label><br />
                    <input
                      value={demoForm.schoolName}
                      onChange={e => setDemoForm(f => ({ ...f, schoolName: e.target.value }))}
                      placeholder="e.g. RI TEST SCHOOL"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="modal-input-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Admin Email *</label><br />
                    <input
                      type="email"
                      value={demoForm.email}
                      onChange={e => setDemoForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="e.g. principal@school.org"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="modal-input-group">
                    <label>First Name *</label><br />
                    <input
                      value={demoForm.firstName}
                      onChange={e => setDemoForm(f => ({ ...f, firstName: e.target.value }))}
                      placeholder="First Name"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="modal-input-group">
                    <label>Last Name *</label><br />
                    <input
                      value={demoForm.lastName}
                      onChange={e => setDemoForm(f => ({ ...f, lastName: e.target.value }))}
                      placeholder="Last Name"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="modal-input-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Street Address *</label><br />
                    <input
                      value={demoForm.address}
                      onChange={e => setDemoForm(f => ({ ...f, address: e.target.value }))}
                      placeholder="e.g. 1234 Main Street, Providence, RI 02901"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="modal-input-group">
                    <label>State (2-letter) *</label><br />
                    <input
                      value={demoForm.state}
                      onChange={e => setDemoForm(f => ({ ...f, state: e.target.value.toUpperCase().slice(0, 2) }))}
                      placeholder="e.g. RI"
                      maxLength={2}
                      style={{ width: '100%', textTransform: 'uppercase' }}
                    />
                  </div>
                </div>

                <div className="modal-buttons" style={{ marginTop: 20 }}>
                  <button
                    onClick={handleCreateDemoSchool}
                    disabled={demoLoading}
                    style={{ opacity: demoLoading ? 0.7 : 1 }}
                  >
                    {demoLoading ? 'Creating...' : 'Create Demo School'}
                  </button>
                  <button onClick={closeDemoModal}>Cancel</button>
                </div>
              </>
            ) : (
              /* Success Card */
              <>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontSize: 48 }}>✅</div>
                  <h4 style={{ color: '#27ae60', marginTop: 8 }}>Demo School Created!</h4>
                </div>
                <div style={{ background: '#f0fff4', border: '1px solid #a8e6c1', borderRadius: 8, padding: '16px 20px', marginBottom: 20 }}>
                  <div style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>School</span><br />
                    <strong style={{ fontSize: 16 }}>{demoResult.school?.name}</strong>
                  </div>
                  <div style={{ borderTop: '1px solid #c3e6cb', paddingTop: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Login Credentials</span><br />
                    <div style={{ fontFamily: 'monospace', fontSize: 15, marginTop: 6 }}>
                      <div>📧 <strong>{demoResult.credentials?.email}</strong></div>
                      <div>🔑 <strong>{demoResult.credentials?.password}</strong></div>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: '#555' }}>
                    Login at <a href="https://admin.angelwatchedu.org" target="_blank" rel="noreferrer">admin.angelwatchedu.org</a>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const text = `School: ${demoResult.school?.name}\nEmail: ${demoResult.credentials?.email}\nPassword: ${demoResult.credentials?.password}\nLogin: https://admin.angelwatchedu.org`;
                    navigator.clipboard.writeText(text);
                  }}
                  style={{ marginBottom: 10, width: '100%', background: '#3498db', color: '#fff', border: 'none', borderRadius: 6, padding: '10px', cursor: 'pointer' }}
                >
                  📋 Copy Credentials
                </button>
                <div className="modal-buttons">
                  <button onClick={closeDemoModal}>Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Change Admin Modal */}
      {isChangeAdminModal && changeAdminSchool && (
        <div className="edit-modal" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: 520, width: '95%', padding: 28 }}>
            <h4 style={{ marginBottom: 6 }}>
              {adminModalMode === 'transfer' ? 'Transfer School Admin' : 'Add School Admin'}
            </h4>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              <strong>{changeAdminSchool.name}</strong>
            </p>

            {/* Show EVERY current admin. The list used to show one email per school, so a
                sysadmin could transfer the role away from admins they were never shown. */}
            {(() => {
              const admins = changeAdminSchool.admins?.length
                ? changeAdminSchool.admins
                : (changeAdminSchool.email ? [{ id: 'primary', email: changeAdminSchool.email }] : []);
              return (
                <>
                  <div style={{ background: '#f8f9fa', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 14 }}>
                    <span style={{ color: '#666' }}>
                      {admins.length === 1 ? 'Current admin: ' : `Current admins (${admins.length}): `}
                    </span>
                    {admins.length === 0 ? (
                      <strong>None</strong>
                    ) : (
                      <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
                        {admins.map(a => (
                          <li key={a.id || a.email}>
                            <strong>{a.email}</strong>
                            {a.firstName || a.lastName ? (
                              <span style={{ color: '#666' }}> — {[a.firstName, a.lastName].filter(Boolean).join(' ')}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {adminModalMode === 'transfer' && admins.length > 0 && (
                    <div style={{ background: '#fff8ec', border: '1px solid #f5c98a', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#8a5a12' }}>
                      <strong>Heads up:</strong> transferring will demote{' '}
                      {admins.length === 1 ? 'this admin' : `all ${admins.length} of these admins`} to staff.
                      To grant admin without removing anyone, use <em>Add Admin</em> instead.
                    </div>
                  )}
                </>
              );
            })()}

            {changeAdminError && (
              <div style={{ background: '#fff0f0', border: '1px solid #ffcdcd', borderRadius: 6, padding: '10px 14px', marginBottom: 14, color: '#c0392b', fontSize: 14 }}>
                {changeAdminError}
              </div>
            )}

            <div className="modal-input-group" style={{ marginBottom: 12, position: 'relative' }}>
              <label>
                {adminModalMode === 'transfer'
                  ? 'Search for the user to transfer admin to'
                  : 'Search for the user to add as an admin'}
              </label><br />
              <input
                value={adminSearchQuery}
                onChange={(e) => handleAdminSearch(e.target.value)}
                placeholder="Type at least 2 characters..."
                autoComplete="off"
                style={{ width: '100%' }}
              />
              {adminSearchLoading && (
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Searching...</div>
              )}
              {adminSearchResults.length > 0 && !selectedNewAdmin && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                  background: '#fff', border: '1px solid #ddd', borderRadius: 6,
                  maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  {adminSearchResults.map(user => (
                    <div
                      key={user.id}
                      onClick={() => {
                        setSelectedNewAdmin(user);
                        setAdminSearchQuery(user.email);
                        setAdminSearchResults([]);
                      }}
                      style={{
                        padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                        fontSize: 14
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = '#f0f7ff'}
                      onMouseOut={(e) => e.currentTarget.style.background = '#fff'}
                    >
                      <div><strong>{user.name}</strong></div>
                      <div style={{ color: '#666', fontSize: 12 }}>{user.email}</div>
                      {user.schoolId && user.schoolId !== changeAdminSchool._id && (
                        <div style={{ color: '#e67e22', fontSize: 11 }}>Currently in another school</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedNewAdmin && (
              <div style={{
                background: '#f0fff4', border: '1px solid #a8e6c1', borderRadius: 6,
                padding: '10px 14px', marginBottom: 16, fontSize: 14
              }}>
                <span style={{ color: '#27ae60', fontWeight: 600 }}>New admin: </span>
                {selectedNewAdmin.name} ({selectedNewAdmin.email})
                <span
                  onClick={() => { setSelectedNewAdmin(null); setAdminSearchQuery(''); }}
                  style={{ float: 'right', cursor: 'pointer', color: '#999' }}
                >
                  &times;
                </span>
              </div>
            )}

            <div className="modal-buttons" style={{ marginTop: 20 }}>
              {moveConfirmNeeded ? (
                <button
                  onClick={() => { setMoveConfirmNeeded(false); handleChangeAdmin(true); }}
                  disabled={changeAdminLoading}
                  style={{ opacity: changeAdminLoading ? 0.5 : 1 }}
                >
                  {changeAdminLoading ? 'Moving...' : 'Move them to this school'}
                </button>
              ) : (
                <button
                  onClick={() => handleChangeAdmin(false)}
                  disabled={!selectedNewAdmin || changeAdminLoading}
                  style={{ opacity: (!selectedNewAdmin || changeAdminLoading) ? 0.5 : 1 }}
                >
                  {changeAdminLoading
                    ? (adminModalMode === 'transfer' ? 'Transferring...' : 'Adding...')
                    : (adminModalMode === 'transfer' ? 'Transfer Admin' : 'Add as Admin')}
                </button>
              )}
              <button onClick={closeChangeAdminModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={isOpenNotification}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={successMessage ? "success" : "error"} sx={{ width: '100%' }}>
          {successMessage || errorMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default SchoolLists;
